import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSession } from '../../state/session.tsx';
import { loadActiveQuickQuote, loadPendingQuickQuote } from '../../state/quick-quote-recovery.ts';
import { companyHasPlanFeature } from '../plan-gate.tsx';
import { useStartWalkthrough, type StartWalkthroughContext } from '../../hooks/use-start-walkthrough.ts';
import { Alert, Button, classes } from '../ui.tsx';
import { PLANS, minimumPlanFor } from '@shared/constants/index.ts';

/**
 * The three ways a quote gets started, in the order they are offered.
 *
 * Magic Quote first because it is the one somebody is looking for on a phone
 * in a driveway, and the one with the least typing. Manual last because it is
 * the fallback every other route lands in anyway: the editor.
 */
export const QUOTE_METHODS = ['magic', 'quick', 'manual'] as const;
export type QuoteMethod = (typeof QUOTE_METHODS)[number];

/** Which gate is closed on a method, when one is. Mirrors `Company.magicQuoteUnavailableReason`. */
export type QuoteMethodGate = 'plan' | 'ai_off' | 'feature_off' | 'unconfigured';

export interface QuoteMethodOption {
  method: QuoteMethod;
  available: boolean;
  /** Null exactly when `available` is true. */
  gate: QuoteMethodGate | null;
  /**
   * This browser holds unfinished work under this method that its panel can
   * pick back up — today, a Quick Quote whose parse was paid for. The page
   * opens on such a method unless the URL asks for another.
   */
  resumable?: true;
}

export function isQuoteMethod(value: string | null | undefined): value is QuoteMethod {
  return QUOTE_METHODS.some((method) => method === value);
}

/**
 * What this person can start a quote with, and why not where they cannot.
 *
 * Each method has its own gate and its own reason, so this is computed here
 * once rather than by every screen that offers a way in:
 *
 *  - Manual needs nothing but `quotes:write`, which the page already needs.
 *  - Quick Quote is a plan feature (Starter and up) that also needs the
 *    deployment to have a model provider and the company's two AI switches on.
 *    The Worker's capability endpoint is the authority while a Quick Quote is
 *    running; this is only deciding whether to offer the door.
 *  - Magic Quote's four-way answer is computed server-side already
 *    (`magicQuoteAvailable`), because a client deriving it from the parts gets
 *    to be wrong in a way that sends somebody into a house with a camera.
 */
export function useQuoteMethods(): QuoteMethodOption[] {
  const { company, currentLocation, can } = useSession();
  const writer = can('quotes:write');

  /**
   * A Quick Quote this browser started and has not finished — a parse whose
   * reply was lost, or a session waiting on its answers. The panel's recovery
   * protocol deliberately keeps that work usable while AI is off or the
   * provider is gone (structured answers, Room Check and generation are
   * deterministic and spend nothing), so those two gates must not hide the
   * door to it: four credits already paid are not forfeited to a switch.
   *
   * The plan and the Quick Quote switch are different: the Worker's
   * `savedQuickQuoteAllowed` keeps both as hard gates on a saved session's
   * actions too, so behind either the card stays locked with its reason
   * rather than opening on a draft that can go nowhere.
   */
  const savedQuickQuote =
    company !== null &&
    (() => {
      const scope = { companyId: company.id, locationId: currentLocation?.id ?? null };
      return loadPendingQuickQuote(scope) !== null || loadActiveQuickQuote(scope) !== null;
    })();

  const quickGate: QuoteMethodGate | null = !companyHasPlanFeature(company, 'quick_quote')
    ? 'plan'
    : company?.aiConfigured === false
      ? 'unconfigured'
      : company?.aiSettings?.enabled === false
        ? 'ai_off'
        : company?.aiSettings?.quickQuotes === false
          ? 'feature_off'
          : null;

  const magicGate: QuoteMethodGate | null =
    company?.magicQuoteAvailable === false ? (company.magicQuoteUnavailableReason ?? 'feature_off') : null;

  return [
    { method: 'magic', available: writer && magicGate === null, gate: magicGate },
    savedQuickQuote && (quickGate === null || quickGate === 'ai_off' || quickGate === 'unconfigured')
      ? { method: 'quick', available: writer, gate: null, resumable: true }
      : { method: 'quick', available: writer && quickGate === null, gate: quickGate },
    { method: 'manual', available: writer, gate: null },
  ];
}

/**
 * The cards at the top of "New quote": pick how this quote is going to be
 * made, based on what this person is actually allowed to do.
 *
 * Unavailable methods are shown — greyed, with the reason and the way to fix
 * it — only to the owner, because the owner is the one person who can act on
 * "upgrade the plan" or "turn AI back on". To everybody else a locked card is
 * an advertisement for something they cannot buy, so for them it is simply not
 * there. The picker disappears altogether when there is only one thing to
 * pick: a choice with one option is a heading.
 *
 * Radio inputs rather than buttons, so the choice reads as a choice to a
 * screen reader and arrow keys move between the cards (spec §27).
 */
export function QuoteMethodPicker({
  options,
  value,
  onChange,
  onLeave,
}: {
  options: readonly QuoteMethodOption[];
  value: QuoteMethod;
  onChange: (method: QuoteMethod) => void;
  /** Asked before a locked card's link leaves the page; it calls `go` to proceed. */
  onLeave?: (go: () => void) => void;
}) {
  const { t } = useTranslation();
  const { user } = useSession();
  const owner = user?.role === 'owner';

  const shown = options.filter((option) => option.available || owner);
  if (shown.length < 2) return null;

  return (
    <fieldset className="card mb-6">
      <legend className="mb-1 text-lg font-semibold">{t('newQuote.method.heading')}</legend>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('newQuote.method.hint')}</p>
      <div className="grid gap-3 sm:grid-cols-3">
        {shown.map((option) => (
          <MethodCard
            key={option.method}
            option={option}
            checked={value === option.method}
            onSelect={() => onChange(option.method)}
            onLeave={onLeave}
          />
        ))}
      </div>
    </fieldset>
  );
}

function MethodCard({
  option,
  checked,
  onSelect,
  onLeave,
}: {
  option: QuoteMethodOption;
  checked: boolean;
  onSelect: () => void;
  onLeave?: (go: () => void) => void;
}) {
  const { t } = useTranslation();
  const { method, available, gate } = option;
  // "AI · Starter+" is the lock label. Once the effective plan already
  // includes the assist (a Business trial, a paid plan), the card does not
  // keep saying a lower plan is required.
  const badge = t(
    method !== 'manual' && gate !== 'plan'
      ? `newQuote.method.${method}.badgeIncluded`
      : `newQuote.method.${method}.badge`,
  );

  return (
    <label
      className={classes(
        'relative flex flex-col gap-1 rounded-lg border p-4',
        available ? 'cursor-pointer' : 'cursor-not-allowed opacity-70',
        checked
          ? 'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)] ring-1 ring-[color:var(--color-brand)]'
          : 'border-[color:var(--color-line)] bg-white hover:border-[color:var(--color-brand)]/60',
      )}
    >
      <input
        type="radio"
        name="quote-method"
        className="sr-only"
        value={method}
        checked={checked}
        disabled={!available}
        onChange={onSelect}
      />
      <span className="flex items-start justify-between gap-2">
        <span className="font-semibold">{t(`newQuote.method.${method}.title`)}</span>
        {badge ? (
          <span className="badge shrink-0 border-[color:var(--color-line)] text-[color:var(--color-muted)]">
            {badge}
          </span>
        ) : null}
      </span>
      <span className="text-sm text-[color:var(--color-muted)]">{t(`newQuote.method.${method}.description`)}</span>
      {gate ? <LockedReason method={method} gate={gate} onLeave={onLeave} /> : null}
    </label>
  );
}

/**
 * Why a card is locked, said in the terms the owner can act on: `plan` is the
 * only gate with a purchase behind it, the two switches live in Settings → AI,
 * and `unconfigured` is the deployment's own missing model key, which nobody
 * using the app can do anything about — so that one gets no link.
 */
function LockedReason({
  method,
  gate,
  onLeave,
}: {
  method: QuoteMethod;
  gate: QuoteMethodGate;
  onLeave?: (go: () => void) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const feature = method === 'magic' ? 'magic_quote' : 'quick_quote';
  const plan = PLANS[minimumPlanFor(feature)].name;
  const href =
    gate === 'plan' ? '/settings#settings-billing' : gate === 'unconfigured' ? null : '/settings#settings-ai';

  return (
    <span className="mt-1 text-sm">
      <span className="text-[color:var(--color-muted)]">{t(`newQuote.method.locked.${gate}`, { plan })}</span>{' '}
      {href ? (
        // A link inside a label would otherwise also toggle the (disabled)
        // radio; stopping propagation keeps the click on the link only.
        <Link
          to={href}
          className="font-semibold text-[color:var(--color-brand)] underline"
          onClick={(event) => {
            event.stopPropagation();
            // A new tab or window leaves nothing behind, so only a plain
            // primary click is asked about.
            if (!onLeave || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
              return;
            }
            event.preventDefault();
            onLeave(() => navigate(href));
          }}
        >
          {t(gate === 'plan' ? 'newQuote.method.locked.upgradeCta' : 'newQuote.method.locked.settingsCta', { plan })}
        </Link>
      ) : null}
    </span>
  );
}

/**
 * The Magic Quote panel on "New quote": what a walkthrough costs, then Start.
 *
 * The customer is optional here on purpose. A cleaner who knocked on a door
 * has nobody on file to pick, and standing in a driveway typing a name into a
 * form is the worst possible moment to ask for one: the walk itself is about
 * to say who this is. The record gets made afterwards, from what was actually
 * said, on the review screen.
 */
export function MagicQuoteStartPanel({
  settling = false,
  ...context
}: StartWalkthroughContext & {
  /**
   * The page is still deciding which property this is for — a customer was
   * just added and their billing address may be about to become one. Starting
   * in that gap would walk a property the record is a second away from
   * naming, and the walkthrough would never know which one it was.
   */
  settling?: boolean;
}) {
  const { t } = useTranslation();
  const walkthrough = useStartWalkthrough(context);

  return (
    <section className="card mb-6 flex flex-col gap-4" aria-labelledby="magic-quote-heading">
      <h2 id="magic-quote-heading" className="text-lg font-semibold">
        {t('newQuote.method.magic.title')}
      </h2>
      <p className="text-sm text-[color:var(--color-muted)]">{t('magicQuote.home.pitch')}</p>
      {/* Said before the walk, not after it. A walkthrough is the only assist
          whose cost scales with the job — thirty photos is thirty actions —
          and an estimator deciding whether to walk a small job deserves to
          know that in advance rather than find it on the meter. */}
      <p className="text-sm text-[color:var(--color-muted)]">{t('magicQuote.home.costNote')}</p>

      {/* No customer is a supported way to start, not a missing field, so the
          button says which of the two is about to happen rather than sitting
          there disabled with nothing to explain it. */}
      {!context.customerId ? (
        <p className="text-sm text-[color:var(--color-muted)]">{t('magicQuote.home.noCustomerHint')}</p>
      ) : null}

      {/* Starting offline is a supported way to work, not a degraded one — but
          the customer list is a network read, so a cleaner who cannot find a
          name they know is on file deserves to know why before they conclude
          the record is missing. */}
      {!walkthrough.online ? (
        <p className="text-sm text-[color:var(--color-muted)]">{t('magicQuote.home.offlineStartHint')}</p>
      ) : null}

      {walkthrough.dialog}
      {walkthrough.error ? (
        <Alert tone="error" title={t('magicQuote.home.startErrorTitle')}>
          {walkthrough.error.message}
        </Alert>
      ) : null}

      <div>
        <Button variant="primary" onClick={walkthrough.start} busy={walkthrough.isPending || settling}>
          {context.customerId ? t('magicQuote.home.start') : t('magicQuote.home.startWithoutCustomer')}
        </Button>
      </div>
    </section>
  );
}
